import { Node, Edge, NodeLabelSettings, EdgeLabelSettings } from "../types";
import { iconMap, colorMap } from "../GraphVizConstants";
import {
  parseJSONSafely,
  htmlTitle,
  generateCategoryTableFromJSON,
  generateAggregationDataHTML,
  generatePropertiesTableFromJSON,
  generateCompanyCommentHTML,
  generateTenantCasesHTML,
} from "../HoverDataUtils";
import { getCognitoGroups } from "../jwtUtils";
import { GraphType } from "../graphType";
import { SearchTerm } from '../SearchHighlightPanel';
import i18n from 'i18next';
import { hasRecentEmploymentSupport } from '../employmentSupport';
import { getCompanyRoleTranslationKey } from '../localization';
import { buildCarNodeLabel, getCarNodeLabelValues } from './carNodeUtils';

const getIntlLocale = (language?: string) => language === 'sv' ? 'sv-SE' : 'en-SE';

const formatGraphDate = (value: string, language?: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(getIntlLocale(language), { year: 'numeric', month: 'short', day: 'numeric' });
};

const getCompanyRole = (value: string) => {
  const translationKey = getCompanyRoleTranslationKey(value);
  return translationKey && i18n.exists(translationKey) ? i18n.t(translationKey) : value;
};

export const ApplyNodeStyling = (
  node: any,
  orgNumber: string,
  graphMetaData: any,
  highlightedNodes?: Set<string>,
  newlyAddedNodes?: Set<string>,
  showNewAdditions?: boolean,
  expandedNodes?: Set<string>,
  nodeLabelSettings?: NodeLabelSettings,
  isDarkMode: boolean = false,
  showCompanyComments: boolean = true,
  language: string | undefined = i18n.resolvedLanguage,
) => {
  const CurrentDebtPerType = node.properties.debt_current_per_type
    ? parseJSONSafely(node.properties.debt_current_per_type)
    : [];

  const startNode = graphMetaData?.sourceNodeId;
  const endNode = graphMetaData?.targetNodeId;
  const isHighlighted = highlightedNodes?.has(node.id) || false;
  const isNewlyAdded = showNewAdditions && newlyAddedNodes?.has(node.id) || false;
  const isExpanded = expandedNodes?.has(node.id) || false;
  
  // Define text color based on mode
  const defaultTextColor = isDarkMode ? '#ecf0f1' : '#000000';

  // Generate hover content with debt information and aggregation data
  const debtTableHTML = generateCategoryTableFromJSON(CurrentDebtPerType);
  const aggregationHTML = generateAggregationDataHTML(node.properties, node.id === startNode, isExpanded);
  const tenantCasesHTML = generateTenantCasesHTML(node.properties);
  const commentHTML = showCompanyComments
    ? generateCompanyCommentHTML(
        typeof node.properties?.comment_markdown === "string" ? node.properties.comment_markdown : undefined,
        typeof node.properties?.comment_updated_at === "string" ? node.properties.comment_updated_at : undefined,
      )
    : "";
  const employmentSupportHTML = node.type === 'Company' && hasRecentEmploymentSupport(node.properties)
    ? `<p>${i18n.t('employmentSupport.badge')}</p>` : '';
  const combinedHTML = employmentSupportHTML + tenantCasesHTML + debtTableHTML + aggregationHTML + commentHTML;
  
  node.title = htmlTitle(combinedHTML);

  // Store highlighting state for later use in label styling
  node.isSearchHighlighted = isHighlighted;
  node.isNewlyAdded = isNewlyAdded;
  
  // Default label settings (all enabled if not provided)
  const labelSettings = nodeLabelSettings || {
    showOrganizationNumber: true,
    showOrganizationName: true,
    showKonkursLikvidation: true,
    showSkuldsaldo: true,
    showAddress: true,
    showName: true,
    showFunktionarNamn: true,
    showFunktionarPersonalNumber: true,
  };
  
  switch (node.type) {
    case "Address":
      node.icon = iconMap.Address;
      node.color = iconMap.Address.shapeColor;
      node.label = null;
      node.size = iconMap.Address.size;

      let Addresslabel = ``;
      if (
        node.id === startNode
      ) {
        Addresslabel += `${i18n.t('graph.nodeLabels.startingNode')}\n`;
      }

      // Only show address details if showAddress is enabled
      if (labelSettings.showAddress) {
        Addresslabel += `${node.properties.street} \n ${node.properties.postal_address} \n ${node.properties.postal_code ?? ""}`;
      }

      node.label = Addresslabel;
      break;

    case "FunktionarPerson":
      node.icon = iconMap.FunktionarPerson;
      node.color = iconMap.FunktionarPerson.shapeColor;
      node.label = null;
      node.size = iconMap.FunktionarPerson.size;

      let funktionarLabel = ``;
      if (labelSettings.showFunktionarNamn && node.properties.funktionar_namn) {
        funktionarLabel += `${node.properties.funktionar_namn}`;
      }
      if (labelSettings.showFunktionarPersonalNumber && node.properties.funktionar_personal_number) {
        if (funktionarLabel !== '') funktionarLabel += '\n';
        const personalNumber = String(node.properties.funktionar_personal_number);
        if (personalNumber.length > 12) {
          funktionarLabel += i18n.t('graph.nodeLabels.emigrated');
        } else {
          funktionarLabel += `${node.properties.funktionar_personal_number}`;
        }
      }
      node.label = funktionarLabel;
      break;
    case "PhoneNumber":
      node.icon = iconMap.PhoneNumber;
      node.color = iconMap.PhoneNumber.shapeColor;
      node.label = null;
      node.size = iconMap.PhoneNumber.size;

      let PhoneLabel = ``;
      // Only show name if showName is enabled
      if (labelSettings.showName) {
        PhoneLabel += `${node.name}`;
      }

      node.label = PhoneLabel;
      break;
    case "CarRegNumber":
      node.icon = iconMap.CarRegNumber;
      node.color = iconMap.CarRegNumber.shapeColor;
      node.label = null;
      node.size = iconMap.CarRegNumber.size;

      if (labelSettings.showName) {
        const carLabelValues = getCarNodeLabelValues(node);
        node.label = buildCarNodeLabel({
          ...carLabelValues,
          leasedFromLabel: i18n.t('graph.nodeLabels.leasedFrom'),
        });
      } else {
        node.label = '';
      }
      break;

    case "Case":
      node.icon = iconMap.CaseNumber;
      node.color = iconMap.CaseNumber.shapeColor;
      node.label = null;
      node.size = iconMap.CaseNumber.size;

      let CaseLabel = ``;
      // Only show name if showName is enabled
      if (labelSettings.showName) {
        CaseLabel += `${node.name}`;
      }

      node.label = CaseLabel;
      break;

    case "BankAccount":
      node.icon = iconMap.BankAccount;
      node.color = iconMap.BankAccount.shapeColor;
      node.label = null;
      node.size = iconMap.BankAccount.size;

      let BankAccountLabel = ``;
      // Only show name if showName is enabled
      if (labelSettings.showName) {
        BankAccountLabel += `${node.name}`;
      }

      node.label = BankAccountLabel;
      break;

    case "Email":
      node.icon = iconMap.Email;
      node.color = iconMap.Email.shapeColor;
      node.label = null;
      node.size = iconMap.Email.size;

      let EmailLabel = ``;
      // Only show name if showName is enabled
      if (labelSettings.showName) {
        EmailLabel += `${node.name}`;
      }

      node.label = EmailLabel;
      break;

    case "Company":
      if (
        node.id === startNode
      ) {
        node.icon = iconMap.Company.starting;
        node.color = colorMap.shapeColor.Company.starting;
        node.size = iconMap.Company.starting.size;
      } else if (node.id === endNode) {
        node.icon = iconMap.Company.ending;
        node.color = colorMap.shapeColor.Company.ending;
        node.size = iconMap.Company.ending.size;
      }
      // CHECK FOR HIGH RISK RELATIONSHIP
      else if (
        node.properties &&
        node.properties.current_keeper === true
      ) {
        node.icon = iconMap.Company.highRiskBoard;
        node.color = colorMap.shapeColor.Company.highRiskBoard;
        node.size = iconMap.Company.highRiskBoard.size;
      }
      // CHECK FOR BANKRUPTCY
      else if (
        node.properties &&
        (node.properties.konkurs_avslutad !== "" ||
          node.properties.konkurs_inledd !== "")
      ) {
        node.icon = iconMap.Company.withBankrupcy;
        node.color = colorMap.shapeColor.Company.withBankrupcy;
        node.size = iconMap.Company.withBankrupcy.size;
      }
      // CHECK FOR LIKVIDATION
      else if (
        node.properties &&
        (
          node.properties.likvidation_avslutad !== "" ||
          node.properties.likvidation_beslutad !== "")
      ) {
        node.icon = iconMap.Company.withLikvidation;
        node.color = colorMap.shapeColor.Company.withLikvidation;
        node.size = iconMap.Company.withLikvidation.size;
      }
      // CHECK FOR SKULDSALDO
      else {
        node.icon =
          node.properties.total_debt > 0
            ? iconMap.Company.withDebt
            : iconMap.Company.default;
        node.color =
          node.properties.total_debt > 0
            ? colorMap.shapeColor.Company.withDebt
            : colorMap.shapeColor.Company.default;

        node.size =
          node.properties.total_debt > 0
            ? iconMap.Company.withDebt.size
            : iconMap.Company.default.size;
      }

      // Build company label parts in order, only including visible items
      const labelParts: string[] = [];
      
      // 1. Organization Number (if enabled)
      if (labelSettings.showOrganizationNumber && node.properties.organization_number) {
        labelParts.push(`${node.properties.organization_number}`);
      }
      
      // 2. Organization Name (if enabled)
      if (labelSettings.showOrganizationName && node.properties.organization_name) {
        labelParts.push(`${node.properties.organization_name}`);
      }

      // 3. Konkurs/Likvidation (if enabled and data exists)
      if (
        labelSettings.showKonkursLikvidation &&
        node.properties &&
        (node.properties.konkurs_avslutad !== "" ||
          node.properties.konkurs_inledd !== "" ||
          node.properties.likvidation_avslutad !== "" ||
          node.properties.likvidation_beslutad !== "")
      ) {
        const konkursLikvidationParts: string[] = [];
        
        // Add konkurs info (prioritize avslutad over inledd)
        if (node.properties.konkurs_avslutad !== "") {
          konkursLikvidationParts.push(i18n.t('graph.nodeLabels.bankruptcyEnded', { date: formatGraphDate(node.properties.konkurs_avslutad, language) }));
        } else if (node.properties.konkurs_inledd !== "") {
          konkursLikvidationParts.push(i18n.t('graph.nodeLabels.bankruptcyStarted', { date: formatGraphDate(node.properties.konkurs_inledd, language) }));
        }

        // Add likvidation info (prioritize avslutad over beslutad)
        if (node.properties.likvidation_avslutad !== "") {
          konkursLikvidationParts.push(i18n.t('graph.nodeLabels.liquidationEnded', { date: formatGraphDate(node.properties.likvidation_avslutad, language) }));
        } else if (node.properties.likvidation_beslutad !== "") {
          konkursLikvidationParts.push(i18n.t('graph.nodeLabels.liquidationDecided', { date: formatGraphDate(node.properties.likvidation_beslutad, language) }));
        }
        
        // Add all konkurs/likvidation info as separate parts
        labelParts.push(...konkursLikvidationParts);
      }
      
      // 4. Skuldsaldo (if enabled and debt exists)
      if (labelSettings.showSkuldsaldo && node.properties.total_debt > 0) {
        // Add empty line before skuldsaldo for better visual separation
        labelParts.push('');
        const debtInMillions = new Intl.NumberFormat(getIntlLocale(language), {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(node.properties.total_debt / 1000000);
        labelParts.push(i18n.t('graph.nodeLabels.debtBalance', { amount: debtInMillions }));
      }
      
      // Join all parts with newlines, automatically removing any trailing empty parts
      node.label = labelParts.join('\n').trim();

      break;
  }
  
  // Apply highlighting styling to nodes
  // New additions (green) take priority initially, then search highlighting (yellow) takes over when green is cleared
  if (node.isNewlyAdded && showNewAdditions) {
    // Green highlighting takes priority when showNewAdditions is true
    node.font = {
      color: '#000000',
      background: '#90EE90', // Light green for newly added nodes
      strokeWidth: 0
    };
  } else if (node.isSearchHighlighted) {
    // Yellow search highlighting shows when green is cleared or not applicable
    node.font = {
      color: '#000000',
      background: '#FFFF00', // Yellow for search results
      strokeWidth: 0
    };
  } else {
    // Use explicit empty-background object instead of undefined.
    // DataSet.update() ignores undefined values, so setting font to undefined
    // would leave the old green/yellow background in place.
    node.font = {
      color: defaultTextColor,
      background: 'transparent',
      strokeWidth: 0
    };
  }
};

export const getAllNodesWithLabels = (
  nodes: Node[],
  orgNumber: string,
  graphMetaData: any,
  highlightedNodes?: Set<string>,
  newlyAddedNodes?: Set<string>,
  showNewAdditions?: boolean,
  expandedNodes?: Set<string>,
  nodeLabelSettings?: NodeLabelSettings,
  isDarkMode: boolean = false,
  showCompanyComments: boolean = true,
  language: string | undefined = i18n.resolvedLanguage,
) => {
  const allNodesWithLabels = nodes.map((node: any) => {
    node.properties.total_debt =
      Number(node.properties.amal_skuldsaldo_current ?? 0) +
      Number(node.properties.emal_skuldsaldo_current ?? 0);

    // Apply node styling
    ApplyNodeStyling(
      node,
      orgNumber,
      graphMetaData,
      highlightedNodes,
      newlyAddedNodes,
      showNewAdditions,
      expandedNodes,
      nodeLabelSettings,
      isDarkMode,
      showCompanyComments,
      language,
    );

    return node;
  });

  return allNodesWithLabels;
}

// Function to apply edge styling based on relationship properties
export const applyEdgeStyling = (edge: any, otherRelationships: any[], graphVizOptions: any, nodes: Node[]) => {
  // Check if the edge goes to a Case node type
  const targetNode = nodes.find(node => node.id === edge.to);
  const isGoingToCase = targetNode?.type === 'Case';

  return {
    color: {
      color: isGoingToCase ? graphVizOptions.edges.edgeColors.caseNumber : graphVizOptions.edges.edgeColors.default, 
      highlight: isGoingToCase ? graphVizOptions.edges.edgeColors.caseNumber : graphVizOptions.edges.edgeColors.highlight,
      hover: isGoingToCase ? graphVizOptions.edges.edgeColors.caseNumber : graphVizOptions.edges.edgeColors.highlight
    },
    width: isGoingToCase ? graphVizOptions.edges.width * 0.5 : graphVizOptions.edges.width, // Thicker for Case connections
    arrows: graphVizOptions.edges.arrows,
    dashes: edge.properties.is_active ? false : (isGoingToCase ? [20, 1] : true), // Different dash pattern for Case connections
    smooth: graphVizOptions.edges.smooth,
    shadow: graphVizOptions.edges.shadow, // Always show shadow for Case connections
    selectionWidth: graphVizOptions.edges.selectionWidth,
    hoverWidth: graphVizOptions.edges.hoverWidth,
    font: graphVizOptions.edges.font
  };
};

// Function to find the relationship with the lowest rank in a group
export const getLowestRankRelationship = (group: any[]) => {
  return group.reduce((lowest, current) => {
    return parseInt(current.properties.rank) <
      parseInt(lowest.properties.rank)
      ? current
      : lowest;
  });
};

export function calculateBinnedDuration(startDate: string | undefined, endDate: string | undefined): string {
  if (!startDate) return i18n.t('common.states.unknown');

  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffInMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

  if (diffInMonths < 6) return i18n.t('graph.durationBins.underSixMonths');
  if (diffInMonths < 12) return i18n.t('graph.durationBins.sevenToTwelveMonths');
  if (diffInMonths < 120) {
    const from = Math.floor(diffInMonths / 12);
    return i18n.t('graph.durationBins.years', { from, to: from + 1 });
  }
  return i18n.t('graph.durationBins.tenPlusYears');
}

export const getAllEdgesWithLabels = (relationships: Edge[], graphVizOptions: any, accessToken: string | undefined, nodes: Node[], newlyAddedEdges?: Set<string>, showNewAdditions?: boolean, edgeLabelSettings?: EdgeLabelSettings, language: string | undefined = i18n.resolvedLanguage) => {
  const relationshipsWithBinnedDuration = relationships.map((relationship) => {
    return {
      ...relationship,
      binnedDuration: calculateBinnedDuration(
        relationship.properties.start_date,
        relationship.properties.end_date
      ),
    };
  });

  // Group relationships by 'from' and 'to'
  const groupedRelationships = relationshipsWithBinnedDuration.reduce((acc, edge) => {
    const key = `${edge.from}_${edge.to}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(edge);
    return acc;
  }, {} as { [key: string]: any[] });

  // Find the lowest rank relationship for each group
  const filteredRelationships = Object.values(groupedRelationships).map((group) =>
    getLowestRankRelationship(group)
  );
  
  // Default edge label settings (all enabled if not provided)
  const labelSettings = edgeLabelSettings || {
    showRole: true,
    showDateRange: true,
    showDuration: true
  };

  const allEdgesWithLabels = filteredRelationships.map((edge: any) => {
    const otherRelationships = edge.properties.other_relationships_json
      ? parseJSONSafely(edge.properties.other_relationships_json)
      : [];

    // Build label parts based on settings
    const labelParts: string[] = [];

    if (edge.properties) {
      if (labelSettings.showRole && edge.properties.funktionar_funk_text) {
        labelParts.push(getCompanyRole(edge.properties.funktionar_funk_text));
      }

      if (labelSettings.showDateRange) {
        const startDate = edge.properties.start_date
          ? formatGraphDate(edge.properties.start_date, language)
          : i18n.t('common.states.unknown');
        const endDate = edge.properties.end_date
          ? formatGraphDate(edge.properties.end_date, language)
          : i18n.t('graph.nodeLabels.current');
        labelParts.push(`${startDate} – ${endDate}`);
      }

      if (labelSettings.showDuration) {
        labelParts.push(i18n.t('graph.nodeLabels.duration', { value: edge.binnedDuration }));
      }
    }

    // Only add spider emoji prefix when there are visible label parts
    let label: string;
    if (labelParts.length > 0) {
      const prefix = otherRelationships.length >= 2 ? `🕸️\n` : '';
      label = prefix + labelParts.join('\n');
    } else {
      // Use a single space — vis-network's isValidLabel() rejects '' (empty
      // string) as invalid and silently keeps the previous label text.  A
      // space passes the check, gets accepted as the new label, and renders
      // as invisible whitespace on the canvas.
      label = ' ';
    }

    // Apply edge styling
    const cognitoGroups = accessToken ? getCognitoGroups(accessToken) : [];
    const edgeStyling = cognitoGroups.includes('GraphImprovements') ? applyEdgeStyling(edge, otherRelationships, graphVizOptions, nodes) : { dashes: edge.properties.is_active ? false : true };

    // Apply green highlighting for newly added edges
    const isNewlyAdded = showNewAdditions && newlyAddedEdges?.has(edge.id) || false;
    const baseWidth = (edgeStyling as any).width ?? graphVizOptions.edges?.width ?? 0.1;
    let finalEdgeStyling: Record<string, any>;
    
    if (isNewlyAdded) {
      finalEdgeStyling = {
        ...edgeStyling,
        color: {
          color: '#32CD32', // Lime green for newly added edges
          highlight: '#228B22', // Darker green for highlight
          hover: '#228B22'
        },
        width: baseWidth + 0.3 // Make slightly thicker than normal
      };
    } else {
      // Explicitly use the original edge styling to ensure green styling is removed.
      // If edgeStyling doesn't include a 'color' key (e.g. non-GraphImprovements users),
      // we must still set one so DataSet.update() overwrites the old green color.
      finalEdgeStyling = { ...edgeStyling, width: baseWidth };
      if (!finalEdgeStyling.color) {
        finalEdgeStyling.color = {
          color: graphVizOptions.edges?.edgeColors?.default || '#848484',
          highlight: graphVizOptions.edges?.edgeColors?.highlight || '#848484',
          hover: graphVizOptions.edges?.edgeColors?.highlight || '#848484'
        };
      }
    }

    return {
      ...edge,
      ...finalEdgeStyling,
      label: label,
      title:
        otherRelationships.length > 0
          ? htmlTitle(generatePropertiesTableFromJSON(otherRelationships))
          : "",
    };
  });

  return allEdgesWithLabels;
}

/**
 * Extract unique graph types from both nodes and relationships
 * - Always includes 'all' as a default option
 * - Extracts all properties from GraphType where value is true
 * - Excludes 'badPaths' as specified in requirements
 * 
 * @param nodes Array of nodes from the graph
 * @param relationships Array of edges from the graph
 * @returns Array of unique graph type names as strings
 */
export const extractUniqueGraphTypes = (relationships: Edge[]): string[] => {
  const uniqueTypes = new Set<string>(['all']);
  
  relationships.forEach(edge => {
    edge.metadata.cumulativeRelationshipTypes.forEach(pathCumulativeRelationships => {
      pathCumulativeRelationships.forEach(relationship => { uniqueTypes.add(relationship); });
    });
  });
  
  var result = Array.from(uniqueTypes).map(GraphType.getGraphType);

  return result;
};

// Function to get a consistent key for hiddenStateStack based on selected graph types
export const getGraphTypesCompositeKey = (types: string[]): string => {
  return types.sort().join('|');
};

// Function to check if a node matches search terms
export const nodeMatchesSearchTerms = (node: Node, searchTerms: SearchTerm[]): boolean => {
  if (searchTerms.length === 0) return false;
  
  const searchableValues: string[] = [];
  
  // Add different searchable properties based on node type
  switch (node.type) {
    case 'Company':
      if (node.properties.organization_number) {
        searchableValues.push(node.properties.organization_number.toString());
      }
      if (node.properties.organization_name) {
        searchableValues.push(node.properties.organization_name.toLowerCase());
      }
      break;
    case 'Address':
      if (node.properties.street) {
        searchableValues.push(node.properties.street.toLowerCase());
      }
      if (node.properties.postal_address) {
        searchableValues.push(node.properties.postal_address.toLowerCase());
      }
      if (node.properties.postal_code) {
        searchableValues.push(node.properties.postal_code.toString());
      }
      break;
    case 'Email':
      // For Email nodes, the email address might be in properties or other fields
      if ((node as any).name) {
        searchableValues.push((node as any).name.toLowerCase());
      }
      if (node.id) {
        searchableValues.push(node.id.toLowerCase());
      }
      break;
    case 'PhoneNumber':
      if ((node as any).name) {
        searchableValues.push((node as any).name.toString());
      }
      if (node.id) {
        searchableValues.push(node.id.toString());
      }
      break;
    case 'FunktionarPerson':
      if ((node as any).name) {
        searchableValues.push((node as any).name.toLowerCase());
      }
      if (node.id) {
        searchableValues.push(node.id.toLowerCase());
      }
      break;
    case 'BankAccount':
      if ((node as any).name) {
        searchableValues.push((node as any).name.toString());
      }
      if (node.id) {
        searchableValues.push(node.id.toString());
      }
      break;
    case 'CarRegNumber':
      const carLabelValues = getCarNodeLabelValues(node);
      Object.values(carLabelValues).forEach((value) => {
        if (typeof value === 'string' || typeof value === 'number') {
          searchableValues.push(value.toString().toLowerCase());
        }
      });
      if (node.id) {
        searchableValues.push(node.id.toLowerCase());
      }
      break;
    case 'Case':
      if ((node as any).name) {
        searchableValues.push((node as any).name.toString().toLowerCase());
      }
      if (node.id) {
        searchableValues.push(node.id.toLowerCase());
      }
      break;
  }
  
  // Check if any search term matches any searchable value (OR logic)
  return searchTerms.some(term => 
    searchableValues.some(value => 
      value.includes(term.value.toLowerCase())
    )
  );
};

// Function to find all nodes matching search terms
export const getHighlightedNodes = (nodes: Node[], searchTerms: SearchTerm[]): Set<string> => {
  const highlightedNodes = new Set<string>();
  
  nodes.forEach(node => {
    if (nodeMatchesSearchTerms(node, searchTerms)) {
      highlightedNodes.add(node.id);
    }
  });
  
  return highlightedNodes;
};
